import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { FilledInput, Button, Icon, IconButton, FormControl, Box, Grid, Divider, Collapse } from '@material-ui/core';
import { Link, useHistory, useParams } from 'react-router-dom';
import { DataGrid } from '@material-ui/data-grid';

import Loader from '../../common/loader';

import AuthContext from '../../context';
import helpers from '../../utils/helpers';
import { get, post } from '../../utils/api-services';
import themeColors from '../../assets/colors';

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    divider: {
        marginBottom: 24
    },
    row: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    controls: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginBottom: 20
    },
    cancel: {
        ...themeColors.grayBtn
    },
    store: {
        marginLeft: 20,
        ...themeColors.blueBtn
    },
    tableContainer: {
        width: '100%',
        marginBottom: 24
    }
}));

const AddPolicies = ({ permissions, submodule }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const { id } = useParams();
    const classes = useStyles();
    const history = useHistory();
    const [selectedPolicies, setSelectedPolicies] = useState([]);
    const [showCurrentData, setShowCurrentData] = useState(false);
    const [state, setState] = useState({
        loading: true,
        currentPolicies: [],
        policies: [],
        permissions: {}
    });

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    useEffect(() => {
        window.document.title = 'Add permissions | IAM';

        makeRequest();
    }, []);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get(`/iam${submodule}/${id}/policiesToAdd`);
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                currentPolicies: response.currentPolicies,
                policies: response.policies
            }));
        } else {
            console.log('[AddPolicies] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const validate = () => {
        if (selectedPolicies.length <= 0) {
            showToast('Select some policies to attach', 'error');
            return false;
        }

        return true;
    }

    const addPolicies = async () => {
        const cond = validate();
        if (cond) {
            showLoader(true);
            const response = await post(`/iam${submodule}/${id}/addPolicies`, { selectedPolicies });
            if (response.status === 200) {
                showToast(response.message);
                history.goBack();
            } else {
                console.log('[AddPolicies] addPolicies error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const getPolicyType = ({ value }) => (value) ? 'CFG managed' : 'User managed';
    const renderName = ({ value, row }) => {
        return (
            <>
                {
                    row?.default &&
                    <Icon fontSize='inherit' style={{ marginRight: 8 }}>{'key_vertical'}</Icon>
                }
                <span>{` ${value}`}</span>
            </>
        );
    }

    const policyColumns = [
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
        { field: 'default', headerName: 'Type', flex: 1, minWidth: 200, valueGetter: getPolicyType, renderCell: renderName },
        { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 }
    ];

    if (state.loading) return <Loader />;

    return (
        <div className={classes.root}>
            <div className={classes.tableContainer}>
                <div className={classes.row}>
                    <h2>{'Current permissions polices'}</h2>

                    <IconButton onClick={() => setShowCurrentData(!showCurrentData)}>
                        <Icon>{showCurrentData ? 'expand_less' : 'expand_more'}</Icon>
                    </IconButton>
                </div>

                <Collapse in={showCurrentData}>
                    <DataGrid
                        rows={state.currentPolicies}
                        columns={policyColumns}
                        pageSize={20}
                        density='compact'
                        autoHeight
                        getRowId={row => row.idiam_policy}
                        disableSelectionOnClick
                    />
                </Collapse>
            </div>

            <Divider className={classes.divider} />

            <div className={classes.tableContainer}>
                <h2>{'Other permission policies'}</h2>
                <DataGrid
                    rows={state.policies}
                    columns={policyColumns}
                    pageSize={20}
                    density='compact'
                    autoHeight
                    getRowId={row => row.idiam_policy}
                    checkboxSelection
                    disableSelectionOnClick
                    onSelectionModelChange={array => setSelectedPolicies(array)}
                />
            </div>

            <div className={classes.controls}>
                <Button
                    variant='contained'
                    className={classes.cancel}
                    onClick={() => history.goBack()}
                >
                    {'Cancel'}
                </Button>

                <Button
                    variant='contained'
                    className={classes.store}
                    onClick={addPolicies}
                >
                    {'Add policies'}
                </Button>

            </div>
        </div>
    );
}

export default AddPolicies;